import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import ErrorRetry from "@/components/ErrorRetry";
import { StatCardsSkeleton, PanelsSkeleton } from "@/components/Skeleton";
import { useNotifications } from "@/context/NotificationContext";
import {
  applyLayout, canMoveDown, canMoveUp, cardPosition, hiddenCardList, moveCard,
  toggleHidden, visibleCards,
} from "@/lib/dashboardLayout";
import {
  AlertTriangle, CalendarClock, RefreshCw, FlaskConical, CheckSquare,
  ListChecks, MessageSquare, CreditCard, ChevronRight, Check, Clock, ArrowRight, Loader2,
  ChevronDown, ChevronUp, Eye, EyeOff, RotateCcw, SlidersHorizontal, X,
} from "lucide-react";

const SEVERITY = {
  high: "bg-red-50 text-red-700 border-red-200",
  medium: "bg-amber-50 text-amber-700 border-amber-200",
  low: "bg-sky-50 text-sky-700 border-sky-200",
};

function Panel({ icon: Icon, title, count, tone = "slate", action, children, testid }) {
  const tones = {
    slate: "text-slate-700 bg-slate-100",
    red: "text-red-700 bg-red-100",
    amber: "text-amber-700 bg-amber-100",
    sky: "text-sky-700 bg-sky-100",
    emerald: "text-emerald-700 bg-emerald-100",
  };
  return (
    <div data-testid={testid} className="bg-card border border-border rounded-lg p-5 flex flex-col h-full">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2.5">
          <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${tones[tone]}`}>
            <Icon className="w-[18px] h-[18px]" />
          </div>
          <h3 className="font-heading text-base font-semibold">{title}</h3>
          {count != null && (
            <span className="text-xs font-semibold tabular-nums bg-secondary text-secondary-foreground rounded-full px-2 py-0.5">{count}</span>
          )}
        </div>
        {action}
      </div>
      <div className="flex-1">{children}</div>
    </div>
  );
}

function Empty({ text }) {
  return <p className="text-sm text-muted-foreground py-2">{text}</p>;
}

function QuickStat({ icon: Icon, tone, label, value, onClick, testid }) {
  const tones = {
    red: "text-red-700 bg-red-100", sky: "text-sky-700 bg-sky-100",
    amber: "text-amber-700 bg-amber-100", emerald: "text-emerald-700 bg-emerald-100",
  };
  return (
    <button data-testid={testid} onClick={onClick} className="w-full h-full text-left bg-card border border-border rounded-lg p-4 hover:shadow-sm hover:-translate-y-[1px] transition-all duration-200">
      <div className="flex items-center justify-between">
        <p className="text-xs uppercase tracking-[0.05em] font-medium text-muted-foreground">{label}</p>
        <div className={`w-7 h-7 rounded-md flex items-center justify-center ${tones[tone]}`}><Icon className="w-4 h-4" /></div>
      </div>
      <p className="font-heading text-2xl font-bold mt-2 tabular-nums">{value}</p>
    </button>
  );
}

function MetricBox({ label, value, tone }) {
  const c = { amber: "text-amber-700", red: "text-red-700" }[tone] || "text-foreground";
  return (
    <div className="bg-secondary/50 rounded-md p-3">
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className={`font-heading text-xl font-bold mt-0.5 tabular-nums ${c}`}>{value}</p>
    </div>
  );
}

/** One editor strip per card, shown only while customising.
 *
 *  Up and Down are real <button> elements, which is the whole accessibility story: a
 *  button is reachable by Tab and activated by Enter or Space because that is what a
 *  button does. A drag surface would need a parallel keyboard path that nothing exercises
 *  and that quietly stops working. There is no drag implementation here at all.
 */
function CardControls({ card, order, onMove, onToggle, busy }) {
  const position = cardPosition(order, card.id);
  return (
    <div className="flex items-center gap-1.5 mb-1.5 rounded-md border border-dashed border-border bg-secondary/40 px-2 py-1.5"
         data-testid={`card-controls-${card.id}`}>
      <span className="text-xs font-medium truncate flex-1">{card.title}</span>
      <span className="text-[10px] text-muted-foreground tabular-nums shrink-0">
        {position} of {order.length}
      </span>
      <button type="button" disabled={busy || !canMoveUp(order, card.id)}
              onClick={() => onMove(card.id, -1)}
              data-testid={`card-up-${card.id}`}
              aria-label={`Move ${card.title} up, currently ${position} of ${order.length}`}
              className="p-1 rounded border border-border hover:bg-card disabled:opacity-30">
        <ChevronUp className="w-3.5 h-3.5" />
      </button>
      <button type="button" disabled={busy || !canMoveDown(order, card.id)}
              onClick={() => onMove(card.id, 1)}
              data-testid={`card-down-${card.id}`}
              aria-label={`Move ${card.title} down, currently ${position} of ${order.length}`}
              className="p-1 rounded border border-border hover:bg-card disabled:opacity-30">
        <ChevronDown className="w-3.5 h-3.5" />
      </button>
      <button type="button" disabled={busy} onClick={() => onToggle(card.id)}
              data-testid={`card-hide-${card.id}`}
              aria-pressed={Boolean(card.hidden)}
              aria-label={`${card.hidden ? "Show" : "Hide"} ${card.title}`}
              className="p-1 rounded border border-border hover:bg-card disabled:opacity-30">
        {card.hidden ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />}
      </button>
    </div>
  );
}

export default function Dashboard() {
  const [ops, setOps] = useState(null);
  const [requests, setRequests] = useState([]);
  const [confirming, setConfirming] = useState("");
  // The error OBJECT, not a message. ErrorRetry owns the wording so no call site can
  // leak a server response onto the screen — see lib/errors.js.
  const [err, setErr] = useState(null);
  const [requestsErr, setRequestsErr] = useState(null);
  const [confirmErr, setConfirmErr] = useState(null);

  const [layout, setLayout] = useState(null);
  const [layoutErr, setLayoutErr] = useState(null);
  const [saveErr, setSaveErr] = useState(null);
  const [customising, setCustomising] = useState(false);
  const [savingLayout, setSavingLayout] = useState(false);

  const navigate = useNavigate();
  const { feed } = useNotifications();

  /* This had NO catch at all, which is why the page could hang on its skeleton forever.
   * An awaited request with no handler is an unhandled rejection: `ops` stayed null, the
   * loading state below rendered indefinitely, and nothing on screen said anything had
   * gone wrong. On the operations dashboard that reads as "nothing needs attention
   * today", which is the most expensive thing this screen could get wrong — the
   * difference between a quiet morning and a lab review nobody saw. */
  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/dashboard/operations");
      setOps(data);
      setErr(null);
    } catch (e) {
      setErr(e);
    }
  }, []);

  /* Was `.catch(() => setRequests([]))` — an empty list is indistinguishable from "no
   * requests pending", so a failure here claimed there was nothing to confirm. On this
   * panel that is somebody waiting to hear back about a visit, told to nobody. */
  const loadRequests = useCallback(async () => {
    try {
      const { data } = await api.get("/appointments/requests");
      setRequests(data);
      setRequestsErr(null);
    } catch (e) {
      setRequestsErr(e);
    }
  }, []);

  const loadLayout = useCallback(async () => {
    try {
      const { data } = await api.get("/staff/dashboard-layout");
      setLayout(data);
      setLayoutErr(null);
    } catch (e) {
      setLayoutErr(e);
    }
  }, []);

  useEffect(() => { load(); loadRequests(); loadLayout(); }, [load, loadRequests, loadLayout]);

  const completeFollowUp = async (id) => {
    await api.post(`/follow-ups/${id}/complete`);
    load();
  };

  /* This used to swallow the error with a bare comment saying the schedule page carried
   * the detailed failure path. That meant a failed confirm looked exactly like a
   * successful one here: the row stayed put and nothing was said. Confirming is the step
   * that claims the slot, so it can genuinely lose a race, and the person pressing the
   * button needs to know it did not happen. */
  const confirmRequest = async (id) => {
    setConfirming(id);
    setConfirmErr(null);
    try {
      await api.post(`/appointments/${id}/confirm`);
      loadRequests();
      load();          // confirming turns a request into a visit on today's list
    } catch (e) {
      setConfirmErr(e);
    }
    setConfirming("");
  };

  /* Saves on every action rather than behind a Save button. There is nothing to lose by
     saving early — the change is one card's position — and a Save button is how somebody
     spends two minutes arranging a screen and then navigates away. */
  const persist = useCallback(async (order, hidden) => {
    setSavingLayout(true);
    setSaveErr(null);
    try {
      const { data } = await api.put("/staff/dashboard-layout", { order, hidden });
      setLayout(data);
    } catch (e) {
      setSaveErr(e);
      loadLayout();     // put back whatever the server actually holds
    }
    setSavingLayout(false);
  }, [loadLayout]);

  const onMove = (id, delta) => {
    const next = moveCard(layout.order, id, delta);
    if (next === layout.order) return;        // at an end: nothing to save
    setLayout({ ...layout, order: next, cards: applyLayout(layout.cards, next, layout.hidden) });
    persist(next, layout.hidden);
  };

  const onToggle = (id) => {
    const nextHidden = toggleHidden(layout.hidden, id);
    setLayout({ ...layout, hidden: nextHidden,
                cards: applyLayout(layout.cards, layout.order, nextHidden) });
    persist(layout.order, nextHidden);
  };

  const onReset = async () => {
    setSavingLayout(true);
    setSaveErr(null);
    try {
      const { data } = await api.delete("/staff/dashboard-layout");
      setLayout(data);
    } catch (e) {
      setSaveErr(e);
    }
    setSavingLayout(false);
  };

  const patientsWaiting = Number(feed?.patient_messages?.awaiting_reply_threads) || 0;
  const unreadThreads = Number(feed?.patient_messages?.unread_threads) || 0;
  const staffUnread = Number(feed?.staff_messages?.unread) || 0;

  /* Every card's body, keyed by the id the server's catalogue uses. A map rather than a
     switch so that a catalogue id with no renderer here is a visible gap rather than a
     crash, which is the direction that keeps the page up when the two drift. */
  const BODIES = useMemo(() => ({
    messages: () => (
      <Panel icon={MessageSquare} title="Messages" count={patientsWaiting + staffUnread}
             tone="sky" testid="panel-messages"
             action={<button data-testid="open-inbox" onClick={() => navigate("/messages")}
                             className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1">
                       Inbox <ChevronRight className="w-3 h-3" />
                     </button>}>
        {patientsWaiting === 0 && staffUnread === 0 ? (
          <Empty text="Nobody is waiting to hear back." />
        ) : (
          <div className="grid sm:grid-cols-2 gap-3">
            <button data-testid="messages-card-patients" onClick={() => navigate("/messages")}
                    className="text-left bg-secondary/50 rounded-md p-3 hover:bg-secondary">
              <p className="text-xs text-muted-foreground">Patients awaiting a reply</p>
              <p className="font-heading text-xl font-bold mt-0.5 tabular-nums">{patientsWaiting}</p>
              <p className="text-[11px] text-muted-foreground mt-1">
                {unreadThreads} unread
              </p>
            </button>
            <button data-testid="messages-card-team" onClick={() => navigate("/messages?view=team")}
                    className="text-left bg-secondary/50 rounded-md p-3 hover:bg-secondary">
              <p className="text-xs text-muted-foreground">Unread from the team</p>
              <p className="font-heading text-xl font-bold mt-0.5 tabular-nums">{staffUnread}</p>
              <p className="text-[11px] text-muted-foreground mt-1">Staff only</p>
            </button>
          </div>
        )}
      </Panel>
    ),

    stat_needs_attention: () => (
      <QuickStat icon={AlertTriangle} tone="red" label="Need Attention"
                 value={ops.needs_attention.length} testid="quick-attention"
                 onClick={() => document.getElementById("needs-attention")?.scrollIntoView({ behavior: "smooth" })} />
    ),

    stat_todays_visits: () => (
      <QuickStat icon={CalendarClock} tone="sky" label="Today's Visits"
                 value={ops.todays_appointments.length} testid="quick-appointments"
                 onClick={() => navigate("/schedule")} />
    ),

    stat_renewals: () => (
      <QuickStat icon={RefreshCw} tone="amber" label="Renewals in 30 Days"
                 value={ops.upcoming_renewals.length} testid="quick-renewals" />
    ),

    /* "Labs Awaiting Review", matching the catalogue. The Customise list named this card
       one thing and the card itself said another, and there is a second card further
       down genuinely called "Lab Reviews" — two cards reading the same name in the
       editor is how somebody hides the wrong one. */
    stat_lab_reviews: () => (
      <QuickStat icon={FlaskConical} tone="emerald" label="Labs Awaiting Review"
                 value={ops.lab_reviews.length} testid="quick-labs"
                 onClick={() => navigate("/labs")} />
    ),

    needs_attention: () => (
      <div id="needs-attention" className="h-full">
        <Panel icon={AlertTriangle} title="Patients Needing Attention" count={ops.needs_attention.length} tone="red" testid="panel-needs-attention">
          {ops.needs_attention.length === 0 ? <Empty text="Nothing needs attention. Great work." /> : (
            <ul className="divide-y divide-border -my-1">
              {ops.needs_attention.map((p) => (
                <li key={p.id} data-testid={`attention-${p.id}`} onClick={() => navigate(`/patients/${p.id}`)} className="flex items-center justify-between gap-3 py-3 cursor-pointer group">
                  <div className="min-w-0">
                    <p className="font-medium group-hover:underline">{p.name} <span className="text-muted-foreground font-normal text-sm">· {p.program}</span></p>
                    <div className="flex flex-wrap gap-1.5 mt-1.5">
                      {p.reasons.map((r, i) => (
                        <span key={i} className={`text-[11px] font-medium rounded-full border px-2 py-0.5 ${SEVERITY[r.severity]}`}>{r.label}</span>
                      ))}
                    </div>
                    {p.reasons[0]?.action && (
                      <p className="text-xs text-muted-foreground mt-2 inline-flex items-center gap-1.5">
                        <ArrowRight className="w-3.5 h-3.5 text-emerald-600 shrink-0" /> {p.reasons[0].action}
                      </p>
                    )}
                  </div>
                  <ChevronRight className="w-4 h-4 text-muted-foreground shrink-0" />
                </li>
              ))}
            </ul>
          )}
        </Panel>
      </div>
    ),

    priorities: () => (
      <Panel icon={ListChecks} title="Top Priorities" count={ops.priorities.length} tone="amber" testid="panel-priorities">
        {ops.priorities.length === 0 ? <Empty text="You're all caught up." /> : (
          <ul className="space-y-3.5">
            {ops.priorities.map((p) => (
              <li key={p.id} data-testid={`priority-${p.id}`} onClick={() => navigate(`/patients/${p.id}`)} className="cursor-pointer group">
                <p className="font-medium text-sm group-hover:underline">{p.name}</p>
                <p className="text-xs text-muted-foreground mt-0.5 inline-flex items-start gap-1.5">
                  <ArrowRight className="w-3.5 h-3.5 text-emerald-600 shrink-0 mt-0.5" />
                  <span>{p.reasons[0]?.action || "Check in with this patient"}</span>
                </p>
              </li>
            ))}
          </ul>
        )}
      </Panel>
    ),

    todays_appointments: () => (
      <Panel icon={CalendarClock} title="Today's Appointments" count={ops.todays_appointments.length} tone="sky" testid="panel-appointments">
        {ops.todays_appointments.length === 0 ? <Empty text="No appointments today." /> : (
          <ul className="space-y-3">
            {ops.todays_appointments.map((a) => (
              <li key={a.id} data-testid={`appt-${a.id}`} className="flex items-center gap-3">
                <span className="text-sm font-semibold tabular-nums w-14 shrink-0">{a.time}</span>
                <div className="min-w-0 cursor-pointer hover:underline" onClick={() => navigate(`/patients/${a.patient_id}`)}>
                  <p className="font-medium text-sm truncate">{a.patient_name}</p>
                  <p className="text-xs text-muted-foreground">{a.type}</p>
                </div>
              </li>
            ))}
          </ul>
        )}
      </Panel>
    ),

    /* Booking requests hold no slot until someone confirms them, so they are a queue
       with a clock on it, not a list. */
    booking_requests: () => (
      <Panel icon={Clock} title="Booking Requests" count={requests.length} tone="amber"
             testid="panel-booking-requests"
             action={requests.length > 0 && (
               <button data-testid="goto-today-requests" onClick={() => navigate("/today")}
                       className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1">
                 Today <ChevronRight className="w-3 h-3" />
               </button>
             )}>
        {requests.length === 0 ? <Empty text="No requests waiting." /> : (
          <>
            <p className="text-xs text-muted-foreground mb-3">
              {requests.length} awaiting confirmation — the time isn't held until you confirm.
            </p>
            <ul className="space-y-3" data-testid="dashboard-requests-list">
              {requests.slice(0, 5).map((r) => (
                <li key={r.id} data-testid={`booking-request-${r.id}`}
                    className="flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <p className="font-medium text-sm truncate">{r.patient_name || "Patient"}</p>
                    <p className="text-xs text-muted-foreground">{r.type} · {r.date} {r.time}</p>
                  </div>
                  <button data-testid={`dashboard-request-confirm-${r.id}`}
                          disabled={confirming === r.id} onClick={() => confirmRequest(r.id)}
                          className="shrink-0 inline-flex items-center gap-1 text-xs font-medium border border-border rounded-md px-2 py-1.5 hover:bg-secondary disabled:opacity-60">
                    {confirming === r.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
                    Confirm
                  </button>
                </li>
              ))}
            </ul>
          </>
        )}
      </Panel>
    ),

    followups: () => (
      <Panel icon={CheckSquare} title="Outstanding Follow-ups" count={ops.outstanding_followups.length} tone="amber" testid="panel-followups">
        {ops.outstanding_followups.length === 0 ? <Empty text="All follow-ups handled." /> : (
          <ul className="space-y-3">
            {ops.outstanding_followups.map((f) => (
              <li key={f.id} data-testid={`followup-${f.id}`} className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-sm font-medium">{f.patient_name}</p>
                  <p className="text-xs text-muted-foreground">{f.task}</p>
                  <p className={`text-[11px] mt-0.5 inline-flex items-center gap-1 ${f.overdue ? "text-red-600" : "text-muted-foreground"}`}>
                    <Clock className="w-3 h-3" /> {f.overdue ? "Overdue" : "Due"} {f.due_date}
                  </p>
                </div>
                <button data-testid={`complete-followup-${f.id}`} onClick={() => completeFollowUp(f.id)} className="shrink-0 inline-flex items-center gap-1 text-xs font-medium border border-border rounded-md px-2 py-1.5 hover:bg-secondary">
                  <Check className="w-3.5 h-3.5" /> Done
                </button>
              </li>
            ))}
          </ul>
        )}
      </Panel>
    ),

    renewals: () => (
      <Panel icon={RefreshCw} title="Upcoming Renewals" count={ops.upcoming_renewals.length} tone="amber" testid="panel-renewals">
        {ops.upcoming_renewals.length === 0 ? <Empty text="No renewals in the next 30 days." /> : (
          <ul className="space-y-3">
            {ops.upcoming_renewals.slice(0, 6).map((r) => (
              <li key={r.id} data-testid={`renewal-${r.id}`} className="flex items-center justify-between cursor-pointer group" onClick={() => navigate(`/patients/${r.id}`)}>
                <div>
                  <p className="text-sm font-medium group-hover:underline">{r.name}</p>
                  <p className="text-xs text-muted-foreground">{r.plan}</p>
                </div>
                <span className={`text-xs font-medium rounded-md px-2 py-1 ${r.status === "lapsed" ? "bg-red-50 text-red-700 border border-red-200" : "bg-amber-50 text-amber-700 border border-amber-200"}`}>
                  {r.status === "lapsed" ? `${Math.abs(r.days)}d lapsed` : `in ${r.days}d`}
                </span>
              </li>
            ))}
          </ul>
        )}
      </Panel>
    ),

    lab_reviews: () => (
      <Panel icon={FlaskConical} title="Lab Reviews" count={ops.lab_reviews.length} tone="emerald" testid="panel-lab-reviews"
        action={<button data-testid="goto-labs" onClick={() => navigate("/today")} className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1">Today <ChevronRight className="w-3 h-3" /></button>}>
        {ops.lab_reviews.length === 0 ? <Empty text="No labs awaiting review." /> : (
          <ul className="space-y-3">
            {ops.lab_reviews.map((l) => (
              <li key={l.id} className="flex items-center justify-between cursor-pointer group" onClick={() => navigate(`/patients/${l.patient_id}`)}>
                <div>
                  <p className="text-sm font-medium group-hover:underline">{l.patient_name}</p>
                  <p className="text-xs text-muted-foreground">{l.panel_name}</p>
                </div>
                <span className="text-xs font-medium bg-amber-50 text-amber-700 border border-amber-200 rounded-full px-2 py-0.5">Pending</span>
              </li>
            ))}
          </ul>
        )}
      </Panel>
    ),

    /* `membership_activity` is ABSENT from the response for any role below owner —
       stripped server-side by scope_dashboard_payload(), not merely unrendered. This
       card is only ever in the layout for a role that gets the field, so the fallback
       below should be unreachable; it is here because an unreachable crash on the
       operations dashboard is still a crash, and a card that renders dashes is a much
       better failure than a blank screen. */
    membership: () => {
      const m = ops.membership_activity || {};
      return (
        <Panel icon={CreditCard} title="Membership Activity" tone="emerald" testid="panel-membership">
          <div className="grid grid-cols-2 gap-3">
            <MetricBox label="Active" value={m.active ?? "—"} />
            <MetricBox label="Retention" value={m.retention_rate == null ? "—" : `${m.retention_rate}%`} />
            <MetricBox label="Renewing" value={m.renewals_upcoming ?? "—"} tone="amber" />
            <MetricBox label="Lapsed" value={m.renewals_lapsed ?? "—"} tone="red" />
          </div>
        </Panel>
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [ops, requests, confirming, patientsWaiting, unreadThreads, staffUnread, navigate]);

  // Failure gets its own state, and a way out of it. Showing the skeleton while an
  // error is live would be a lie dressed as patience.
  if (!ops && err) {
    return (
      <div className="space-y-4" data-testid="dashboard-error">
        <h1 className="font-heading text-3xl font-bold tracking-tight">Operations Center</h1>
        <ErrorRetry
          error={err}
          onRetry={() => { setErr(null); load(); loadRequests(); }}
          title="Today's operations couldn't be loaded"
          note="Nothing here is up to date — don't read an empty screen as a clear day."
          testid="dashboard-error-retry"
        />
      </div>
    );
  }

  /* No layout, no cards. The layout is what says which cards this ROLE may see, so
     falling back to a client-side list would be the one thing this feature promises not
     to do: let a saved or assumed layout show somebody a card their role does not get.
     Failing closed with a way out is the honest answer. */
  if (!layout && layoutErr) {
    return (
      <div className="space-y-4" data-testid="dashboard-layout-error">
        <h1 className="font-heading text-3xl font-bold tracking-tight">Operations Center</h1>
        <ErrorRetry
          error={layoutErr}
          onRetry={() => { setLayoutErr(null); loadLayout(); }}
          title="Your Overview layout couldn't be loaded"
          note="Nothing is shown rather than a guess at which cards belong to your role."
          testid="dashboard-layout-error-retry"
        />
      </div>
    );
  }

  if (!ops || !layout) {
    return (
      <div className="space-y-6" data-testid="dashboard-loading">
        <div>
          <div className="h-3 w-32 bg-secondary rounded animate-pulse" />
          <div className="h-8 w-56 bg-secondary rounded animate-pulse mt-2" />
        </div>
        <StatCardsSkeleton />
        <PanelsSkeleton count={6} />
      </div>
    );
  }

  const dateLabel = new Date(ops.today + "T00:00:00").toLocaleDateString(undefined, {
    weekday: "long", month: "long", day: "numeric",
  });
  const shown = customising ? layout.cards : visibleCards(layout.cards);
  const hiddenOnes = hiddenCardList(layout.cards);

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="text-xs uppercase tracking-[0.05em] font-medium text-muted-foreground">{dateLabel}</p>
          <h1 className="font-heading text-3xl font-bold tracking-tight mt-1">Operations Center</h1>
          <p className="text-sm text-muted-foreground mt-1">What your team should focus on today to improve outcomes and strengthen relationships.</p>
        </div>
        <div className="flex items-center gap-2">
          {customising && (
            <button data-testid="dashboard-reset-layout" onClick={onReset} disabled={savingLayout}
                    className="inline-flex items-center gap-1.5 text-xs font-medium border border-border rounded-md px-2.5 py-2 hover:bg-secondary disabled:opacity-60">
              <RotateCcw className="w-3.5 h-3.5" /> Reset to default
            </button>
          )}
          <button data-testid="dashboard-customise" onClick={() => setCustomising((c) => !c)}
                  aria-pressed={customising}
                  className="inline-flex items-center gap-1.5 text-xs font-semibold border border-border rounded-md px-2.5 py-2 hover:bg-secondary">
            {customising ? <X className="w-3.5 h-3.5" /> : <SlidersHorizontal className="w-3.5 h-3.5" />}
            {customising ? "Done" : "Customise"}
          </button>
        </div>
      </div>

      {customising && (
        <p className="text-xs text-muted-foreground border border-dashed border-border rounded-md px-3 py-2"
           data-testid="dashboard-customise-help">
          Use the arrows to reorder and the eye to show or hide. Every control is a button,
          so Tab and Enter work. Changes save as you make them, for your account only.
          {savingLayout && <Loader2 className="inline w-3 h-3 ml-1.5 animate-spin" />}
        </p>
      )}

      {/* A refresh failed but earlier data is still on screen. Say so, rather than
          letting stale panels pass for current ones. */}
      <ErrorRetry
        error={err}
        variant="inline"
        onRetry={() => { setErr(null); load(); loadRequests(); }}
        note="Showing the last data that loaded."
        testid="dashboard-stale"
      />
      <ErrorRetry
        error={requestsErr}
        variant="inline"
        onRetry={() => { setRequestsErr(null); loadRequests(); }}
        note="Booking requests couldn't be loaded, so this list may be incomplete."
        testid="dashboard-requests-error"
      />
      <ErrorRetry
        error={confirmErr}
        variant="inline"
        onRetry={() => setConfirmErr(null)}
        note="That appointment was not confirmed. The patient has not been told anything."
        testid="dashboard-confirm-error"
      />
      <ErrorRetry
        error={saveErr}
        variant="inline"
        onRetry={() => { setSaveErr(null); loadLayout(); }}
        note="Your layout change was not saved. The arrangement shown is the one on the server."
        testid="dashboard-layout-save-error"
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 items-start" data-testid="dashboard-cards">
        {shown.map((card) => {
          const body = BODIES[card.id];
          if (!body) return null;        // catalogue grew; this client has no renderer yet
          return (
            <div key={card.id}
                 data-testid={`dashboard-card-${card.id}`}
                 className={`${card.span === 2 ? "lg:col-span-2" : ""} ${card.hidden ? "opacity-50" : ""}`}>
              {customising && (
                <CardControls card={card} order={layout.order} onMove={onMove}
                              onToggle={onToggle} busy={savingLayout} />
              )}
              {body()}
            </div>
          );
        })}
      </div>

      {!customising && hiddenOnes.length > 0 && (
        <p className="text-xs text-muted-foreground" data-testid="dashboard-hidden-note">
          {hiddenOnes.length} card{hiddenOnes.length === 1 ? "" : "s"} hidden ·{" "}
          <button onClick={() => setCustomising(true)} className="underline">Customise</button>
        </p>
      )}
    </div>
  );
}
